/**
 * Composable pour charger les données de donation pour une campagne
 * Similaire à useDonationData mais pour les campagnes
 *
 * ✅ Optimisé pour SSR: utilise useFetch pour charger les données côté serveur
 * Les données sont disponibles instantanément au premier rendu (pas de skeleton)
 */
import { logger } from "~/utils/logger";
import { nettoyerMontantsImpact } from "~/utils/montantsImpact";
import {
  buildDirectusAssetUrl,
  useOrganizationPaymentInfo,
  type OrgPaymentProviders,
} from "./useOrganizationPaymentInfo";

export interface CampaignDonationInfo {
  id: number;
  title: string;
  description: string;
  organization: string;
  organizationLogo: string;
  goalAmount: number | null;
  currentAmount: number;
  donorCount: number;
  currency: string;
  image: string | null;
  allowAnonymous: boolean;
  /** Le donateur peut laisser un message avec son don (réglage de la cagnotte). */
  allowDonorMessage: boolean;
  customThankYouMessage: string | null;
  /** Barre de temps : jour de fin, instant de clôture, jours restants, lancement. */
  endDate: string | null;
  closesAt: string | null;
  daysRemaining: number | null;
  startsAt: string | null;
}

interface CampaignDonationData {
  campaign?: {
    id: number;
    title: string;
    slug: string;
    description?: string;
    goal_amount?: number | null;
    currency: string;
    status: string;
    image?: string | null;
    allow_anonymous: boolean;
    /** Réglage PAR CAGNOTTE : le donateur peut laisser un message (défaut vrai). */
    allow_donor_message?: boolean;
    custom_thank_you_message?: string | null;
    /** Date de fin (jour), instant de clôture et jours restants — barre de temps. */
    end_date?: string | null;
    closes_at?: string | null;
    days_remaining?: number | null;
    date_created?: string | null;
    /** Montants d'impact PROPRES à la cagnotte (migration 332), sans héritage de l'asso. */
    impact_options?: unknown;
  };
  organization?: {
    id: string;
    name: string;
    logo?: string | null;
    stripe_account_id?: string | null;
    fee_percentage?: number;
    is_premium?: boolean;
    // Surveillance
    risk_status?: string | null;
    risk_reason?: string | null;
    payment_providers?: OrgPaymentProviders;
  };
  showFiscalInfo?: boolean;
  paymentPage?: {
    amounts?: number[];
    defaultAmountIndex?: number;
    primaryColor?: string | null;
    backgroundColor?: string | null;
    buttonStyle?: string | null;
    submitText?: string | null;
    subscriptionsEnabled?: boolean;
    customFields?: Array<{ type: "fullname" | "email" | "message"; required: boolean; label: string }> | null;
    showFeesOption?: boolean;
    showFiscalInfo?: boolean;
  } | null;
  raised?: number;
  donors_count?: number;
  percent?: number;
}

type Translate = (key: string, params?: Record<string, unknown>) => string;

function emptyCampaignInfo(t: Translate): CampaignDonationInfo {
  return {
    id: 0,
    title: t("campagne.donationData.campagneParDefaut"),
    description: "",
    organization: t("campagne.donationData.organisationParDefaut"),
    organizationLogo: "/newLogo/logo-mark-white.svg",
    goalAmount: null,
    currentAmount: 0,
    donorCount: 0,
    currency: "EUR",
    image: null,
    allowAnonymous: true,
    allowDonorMessage: true,
    customThankYouMessage: null,
    endDate: null,
    closesAt: null,
    daysRemaining: null,
    startsAt: null,
  };
}

/** Champs issus directement de l'entité campagne (titre, montants, image…). */
function mapCampaignFields(
  campaign: CampaignDonationData["campaign"] | undefined,
  t: Translate,
): Pick<
  CampaignDonationInfo,
  | "id"
  | "title"
  | "description"
  | "goalAmount"
  | "currency"
  | "image"
  | "allowAnonymous"
  | "allowDonorMessage"
  | "customThankYouMessage"
  | "endDate"
  | "closesAt"
  | "daysRemaining"
  | "startsAt"
> {
  return {
    id: campaign?.id || 0,
    title: campaign?.title || t("campagne.donationData.campagneParDefaut"),
    description: campaign?.description || "",
    goalAmount: Number(campaign?.goal_amount) || null,
    currency: campaign?.currency || "EUR",
    image: campaign?.image || null,
    allowAnonymous: campaign?.allow_anonymous ?? true,
    allowDonorMessage: campaign?.allow_donor_message !== false,
    customThankYouMessage: campaign?.custom_thank_you_message || null,
    ...mapCampaignEndDate(campaign),
  };
}

/** Données de la barre de temps (date de fin posée sur la cagnotte). */
function mapCampaignEndDate(
  campaign: CampaignDonationData["campaign"] | undefined,
): Pick<CampaignDonationInfo, "endDate" | "closesAt" | "daysRemaining" | "startsAt"> {
  return {
    endDate: campaign?.end_date ?? null,
    closesAt: campaign?.closes_at ?? null,
    daysRemaining: campaign?.days_remaining ?? null,
    startsAt: campaign?.date_created ?? null,
  };
}

/** Champs issus de l'organisation + agrégats temps réel (raised / donors). */
function mapOrgAndAggregateFields(
  data: CampaignDonationData,
  t: Translate,
): Pick<
  CampaignDonationInfo,
  "organization" | "organizationLogo" | "currentAmount" | "donorCount"
> {
  return {
    organization: data.organization?.name || t("campagne.donationData.organisationParDefaut"),
    organizationLogo: data.organization?.logo || "/newLogo/logo-mark-white.svg",
    currentAmount: data.raised || 0,
    donorCount: data.donors_count || 0,
  };
}

/** Construit l'objet d'affichage de la campagne à partir des données SSR. */
function buildCampaignInfo(data: CampaignDonationData | undefined, t: Translate): CampaignDonationInfo {
  if (!data) return emptyCampaignInfo(t);
  return {
    ...mapCampaignFields(data.campaign, t),
    ...mapOrgAndAggregateFields(data, t),
  };
}

export const useCampaignDonationData = (
  campaignIdOrSlug: Ref<string | number>
) => {
  const config = useRuntimeConfig();
  const { t } = useI18n();

  // États d'erreur
  const isCampaignValid = ref(true);
  const isCampaignPaused = ref(false);
  const campaignError = ref("");
  const errorTitle = ref(t("campagne.donationData.erreurTitreDefaut"));
  const hasStripeAccount = ref(true);
  // Cagnotte TROUVÉE mais association sans aucun moyen de paiement actif
  // (« Paiements bientôt disponibles »). À distinguer d'une cagnotte
  // introuvable, qui force aussi `hasStripeAccount = false`.
  const isPaymentsPending = ref(false);

  // ✅ SSR: Utiliser useFetch pour charger les données côté serveur
  // Les données seront disponibles immédiatement au premier rendu
  const {
    data: ssrData,
    status,
    error: fetchError,
    refresh: refreshData,
  } = useFetch<{ success: boolean; data?: CampaignDonationData; message?: string; code?: string }>(
    () => `${config.public.apiUrl}/api/campaigns/public/${campaignIdOrSlug.value}/donation`,
    {
      key: `campaign-donation-${campaignIdOrSlug.value}`,
      // Ne pas exécuter si pas d'ID
      immediate: !!campaignIdOrSlug.value,
      // Retry transitoire (timeout/5xx) — éviter le flaky "Cagnotte non
      // disponible" au premier refresh (Karim 2026-06-21).
      retry: 2,
      retryDelay: 300,
      timeout: 8000,
    }
  );

  // ✅ SSR: Dérivé des données SSR - disponible immédiatement
  const isLoading = computed(() => status.value === "pending");

  // Données chargées - informations de la campagne
  const campaignInfo = computed<CampaignDonationInfo>(() =>
    buildCampaignInfo(ssrData.value?.data, t),
  );

  const organizationId = computed(
    () => ssrData.value?.data?.organization?.id || null,
  );

  // ─── Dérivations paiement (factorisées via useOrganizationPaymentInfo) ──
  const {
    paymentProviders,
    hasActivePaymentProvider,
    stripeAccountId,
    paymentMethodConfigurationId,
    donoorFeeRate,
    donateUs,
    isPremiumPlan,
    organizationLogoUrl,
  } = useOrganizationPaymentInfo(
    computed(() => ssrData.value?.data?.organization ?? null),
  );

  const showFiscalInfo = computed(() => {
    return ssrData.value?.data?.showFiscalInfo ?? false;
  });

  // Options héritées de la "page de paiement" globale de l'orga
  const paymentPage = computed(() => ssrData.value?.data?.paymentPage ?? null);
  const showFeesOption = computed(() => paymentPage.value?.showFeesOption ?? true);
  const subscriptionsEnabled = computed(() => paymentPage.value?.subscriptionsEnabled ?? true);
  const suggestedAmounts = computed(() => paymentPage.value?.amounts ?? null);
  const defaultAmountIndex = computed(() => paymentPage.value?.defaultAmountIndex ?? 0);
  const customFields = computed(() => paymentPage.value?.customFields ?? null);
  const askFullName = computed(() => {
    const f = customFields.value;
    if (!f) return true;
    return f.some((x) => x.type === "fullname");
  });
  const requireFullName = computed(() => {
    return customFields.value?.find((x) => x.type === "fullname")?.required ?? false;
  });
  const askEmail = computed(() => {
    const f = customFields.value;
    if (!f) return true;
    return f.some((x) => x.type === "email");
  });
  const requireEmail = computed(() => {
    return customFields.value?.find((x) => x.type === "email")?.required ?? false;
  });
  // « Message (optionnel) » : réglage PAR CAGNOTTE (Karim 2026-09-30, comme
  // sur /pay). Avant, seule la page de paiement globale de l'association le
  // décidait, via son champ personnalisé « message ».
  const askMessage = computed(() => campaignInfo.value?.allowDonorMessage !== false);
  const primaryColor = computed(() => paymentPage.value?.primaryColor ?? null);
  const buttonStyle = computed(() => paymentPage.value?.buttonStyle ?? null);
  const submitText = computed(() => paymentPage.value?.submitText ?? null);
  // Montants d'impact : ceux de la CAGNOTTE seulement — jamais ceux de la page
  // de paiement de l'asso (« 5 € = un repas » n'a pas de sens sur une toiture).
  const impactOptions = computed(() =>
    nettoyerMontantsImpact(ssrData.value?.data?.campaign?.impact_options),
  );

  /** URL absolue de l'image de la campagne (null si pas d'image). */
  const campaignImageUrl = computed(() => {
    const directusUrl =
      (config.public.apiUrl as string) || "https://api.donoor.org";
    // 1200px : image hero cagnotte sur la page de paiement (affichée ~600-800px).
    return buildDirectusAssetUrl(campaignInfo.value.image, directusUrl, null, 1600);
  });

  /**
   * Pourcentage de progression
   */
  const progressPercentage = computed(() => {
    if (!campaignInfo.value.goalAmount || campaignInfo.value.goalAmount <= 0) {
      return 0;
    }
    const percentage =
      (campaignInfo.value.currentAmount / campaignInfo.value.goalAmount) * 100;
    return Math.min(percentage, 100);
  });

  // Applique l'état d'erreur de fetch sur les refs (404 / 400 paused / autre).
  const applyFetchErrorState = (rawError: unknown) => {
    const error = rawError as {
      statusCode?: number;
      status?: number;
      data?: { code?: string; message?: string };
    };
    logger.error("❌ Erreur chargement campagne:", error);

    if (error?.statusCode === 404 || error?.status === 404) {
      isCampaignValid.value = false;
      errorTitle.value = t("campagne.donationData.cagnotteIndisponibleTitre");
      // Un 404 vient le plus souvent d'un BROUILLON jamais publié (l'API ne
      // sert que les cagnottes publiées) — pas d'une suppression. On l'indique,
      // sinon l'association ne comprend pas pourquoi son widget reste vide.
      campaignError.value = t("campagne.donationData.cagnottePasPublieeMessage");
    } else if (error?.statusCode === 400 || error?.status === 400) {
      const errorCode = error?.data?.code || ssrData.value?.code;
      if (errorCode === "CAMPAIGN_PAUSED") {
        isCampaignPaused.value = true;
        isCampaignValid.value = false;
        errorTitle.value = t("campagne.donationData.campagnePauseTitre");
        campaignError.value =
          error?.data?.message || t("campagne.donationData.campagnePauseMessage");
      } else {
        isCampaignValid.value = false;
        errorTitle.value = t("campagne.donationData.campagneIndisponibleTitre");
        campaignError.value =
          error?.data?.message || t("campagne.donationData.campagneNAcceptePlusMessage");
      }
    } else {
      isCampaignValid.value = false;
      errorTitle.value = t("campagne.donationData.erreurChargementTitre");
      campaignError.value = t("campagne.donationData.erreurChargementMessage");
    }
    hasStripeAccount.value = false;
    isPaymentsPending.value = false;
  };

  // Vérifie qu'AU MOINS UN provider de paiement est actif (Stripe OU PayPal).
  // Avant on bloquait sur l'absence de stripe_account_id seul — une asso
  // PayPal-only était donc affichée comme "indisponible" alors que
  // `paymentProviders.accepts_payments` valait true.
  const applyPaymentProviderState = () => {
    if (!ssrData.value?.data) return;
    if (hasActivePaymentProvider.value) {
      isPaymentsPending.value = false;
      hasStripeAccount.value = true; // gardé pour compat UI ascendante
      isCampaignValid.value = true;
      logger.log(
        "✅ Données de campagne chargées avec succès (SSR), provider actif",
      );
    } else {
      hasStripeAccount.value = false;
      isCampaignValid.value = false;
      isPaymentsPending.value = true;
      errorTitle.value = t("campagne.donationData.paiementsBientotDisponiblesTitre");
      campaignError.value = t("campagne.donationData.paiementsBientotDisponiblesMessage");
      logger.warn(
        "⚠️ Aucun provider de paiement actif (ni Stripe ni PayPal)",
      );
    }
  };

  // ✅ Watcher pour gérer les erreurs et états
  watch(
    [ssrData, fetchError, status],
    () => {
      // Si on est en train de charger, ne pas mettre à jour les états
      if (status.value === "pending") return;

      // Gérer les erreurs fetch
      if (fetchError.value) {
        applyFetchErrorState(fetchError.value);
        return;
      }

      applyPaymentProviderState();
    },
    { immediate: true }
  );

  /**
   * Charger les données de la campagne
   * Maintenu pour compatibilité mais utilise maintenant refresh() en interne
   */
  const loadCampaignData = async () => {
    if (!campaignIdOrSlug.value) {
      logger.warn("⚠️ Pas de campaign ID ou slug fourni");
      return;
    }

    // Si les données sont déjà chargées (SSR), pas besoin de recharger
    if (ssrData.value?.data && status.value === "success") {
      logger.log("✅ Données déjà disponibles via SSR");
      return;
    }

    // Sinon, rafraîchir les données
    await refreshData();
  };

  /**
   * Mettre à jour les données en temps réel (via WebSocket).
   * Appelé quand un nouveau don arrive sur la campagne.
   *
   * Important : on remplace l'enveloppe `data` entière (et `ssrData.value`)
   * par une nouvelle référence pour garantir le déclenchement des computed
   * qui en dépendent. Muter `ssrData.value.data.raised = X` ne suffit pas
   * toujours en SSR/Nuxt (le ref ne re-trigger pas si la racine ne change
   * pas dans certains cas de hydration).
   */
  const updateRealtimeData = (newAmount: number, newDonorCount: number) => {
    const current = ssrData.value;
    if (!current?.data) return;
    ssrData.value = {
      ...current,
      data: {
        ...current.data,
        raised: newAmount,
        donors_count: newDonorCount,
      },
    };
    logger.log(
      `🔄 Données temps réel mises à jour: ${newAmount}€, ${newDonorCount} participants`,
    );
  };

  // Surveillance / Trust & Safety
  const organizationRiskStatus = computed(
    () => ssrData.value?.data?.organization?.risk_status || null,
  );
  const organizationRiskReason = computed(
    () => ssrData.value?.data?.organization?.risk_reason || null,
  );
  const isOrgBlocked = computed(
    () =>
      organizationRiskStatus.value === "suspended" ||
      organizationRiskStatus.value === "banned",
  );

  return {
    // États
    isLoading,
    isCampaignValid,
    isCampaignPaused,
    campaignError,
    errorTitle,
    /**
     * @deprecated nom historique — sémantique réelle = "au moins un
     * provider (Stripe OU PayPal) opérationnel". Préférer `canAcceptPayments`.
     */
    hasStripeAccount,
    canAcceptPayments: hasStripeAccount,
    isPaymentsPending,

    // Surveillance
    organizationRiskStatus,
    organizationRiskReason,
    isOrgBlocked,

    // Données
    campaignInfo,
    organizationId,
    stripeAccountId,
    paymentProviders,
    hasActivePaymentProvider,
    paymentMethodConfigurationId,
    donoorFeeRate,
    donateUs,
    isPremiumPlan,
    showFiscalInfo,
    showFeesOption,
    subscriptionsEnabled,
    suggestedAmounts,
    defaultAmountIndex,
    customFields,
    askFullName,
    requireFullName,
    askEmail,
    requireEmail,
    askMessage,
    primaryColor,
    buttonStyle,
    submitText,
    impactOptions,

    // URLs calculées
    organizationLogoUrl,
    campaignImageUrl,
    progressPercentage,

    // Actions
    loadCampaignData,
    refreshData,
    updateRealtimeData,
  };
};
